import React, { useState } from "react";
import {
  Button, Typography, Box, Collapse, IconButton,
  FormControl, Select, MenuItem, Chip, OutlinedInput,
  Checkbox, ListItemText, TextField,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { MobileDatePicker } from "@mui/x-date-pickers/MobileDatePicker";
import dayjs from "dayjs";

import {
  useGetSchedulePSListQuery,
  useAddSchedulePSMutation,
  useDeleteSchedulePSMutation,
  useUpdateBulkSchedulePSMutation,
  useGetFeederGroupsQuery,
  useGetConfigurationsQuery,
} from "../UserSettings/scheduleApiSlice";
import CSVBulkUpdate from "../UserSettings/CSVBulkUpdate";
import LSPSMasterSwitch from "../OperatorDashboard/LSPSMasterSwitch";

const LABEL_SX = {
  fontSize: "0.6rem",
  fontWeight: 600,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color: "rgba(255,255,255,0.3)",
  mb: "6px",
  whiteSpace: "nowrap",
};

const INPUT_SX = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#0d1117",
    borderRadius: "6px",
    fontSize: "0.88rem",
    color: "#e0e6f0",
    "& fieldset": { borderColor: "rgba(255,255,255,0.1)" },
    "&:hover fieldset": { borderColor: "rgba(255,255,255,0.22)" },
    "&.Mui-focused fieldset": { borderColor: "#FDB940 !important", borderWidth: "1px !important" },
  },
  "& .MuiInputBase-input": { padding: "8px 12px" },
  "& .MuiSvgIcon-root": { color: "rgba(255,255,255,0.4)" },
};

const SELECT_SX = {
  backgroundColor: "#0d1117",
  borderRadius: "6px",
  fontSize: "0.88rem",
  color: "#e0e6f0",
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.1)" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.22)" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#FDB940 !important" },
  "& .MuiSelect-icon": { color: "rgba(255,255,255,0.4)" },
  "& .MuiSelect-select": { padding: "8px 12px" },
};

const MENU_PROPS = {
  PaperProps: {
    sx: {
      backgroundColor: "#1C232C",
      border: "1px solid rgba(255,255,255,0.1)",
      borderRadius: "8px",
      "& .MuiMenuItem-root": {
        fontSize: "0.88rem",
        color: "#e0e6f0",
        "&:hover": { bgcolor: "rgba(255,255,255,0.06)" },
        "&.Mui-selected": { bgcolor: "rgba(253,185,64,0.1)" },
        "&.Mui-selected:hover": { bgcolor: "rgba(253,185,64,0.14)" },
      },
    },
  },
};

function Col({ label, width, minWidth, flex, children }) {
  return (
    <Box sx={{ width, minWidth, flex, flexShrink: width ? 0 : undefined, display: "flex", flexDirection: "column" }}>
      <Typography sx={LABEL_SX}>{label}</Typography>
      {children}
    </Box>
  );
}

export default function SchedulePS() {
  const { data: schedule_data, isSuccess: isScheduleSuccess, refetch } = useGetSchedulePSListQuery();
  const [addSchedulePS, { isSuccess: isAddSuccess }] = useAddSchedulePSMutation();
  const [updateBulkSchedulePS, { isSuccess: isUpdateSuccess, isError: isUpdateError }] = useUpdateBulkSchedulePSMutation();
  const { data: feederGroups = [] } = useGetFeederGroupsQuery();
  const { data: configurations = [] } = useGetConfigurationsQuery();

  const [bulkArray, setBulkArray] = useState([]);
  const [saved, setSaved] = React.useState("Saved!");
  const [csvModalOpen, setCsvModalOpen] = React.useState(false);
  const [deletingId, setDeletingId] = React.useState(null);

  React.useEffect(() => { if (isScheduleSuccess) setBulkArray(schedule_data); }, [isScheduleSuccess, schedule_data]);
  React.useEffect(() => { if (isAddSuccess) refetch(); }, [refetch, isAddSuccess]);
  React.useEffect(() => {
    if (isUpdateSuccess) setSaved("Saved!");
    else if (isUpdateError) setSaved("Save");
  }, [isUpdateSuccess, isUpdateError]);

  const addSchedule = () => {
    addSchedulePS({ body: { day: "Sunday", start_time: "00:00:00", end_time: "21:59:59", target: 0.0 } });
  };

  if (!isScheduleSuccess) return null;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: "20px" }}>
        <Box>
          {/* The master switch sits beside its own tab's title, behind a type-CONFIRM dialog
              (NEU-697, NEU-1225, NEU-1226); the header only shows its status. */}
          <Box sx={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
            <Typography sx={{ fontSize: "1.1rem", fontWeight: 700, color: "#fff", lineHeight: 1.3 }}>
              Peak Shaving Schedules
            </Typography>
            <LSPSMasterSwitch kind="peak_shaving" />
          </Box>
          <Typography sx={{ fontSize: "0.82rem", color: "rgba(255,255,255,0.4)", mt: "3px" }}>
            Time windows when enrolled geysers are capped to protect against grid peak demand.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: "8px", alignItems: "center", flexShrink: 0 }}>
          <Button
            variant="outlined"
            startIcon={<AddIcon sx={{ fontSize: "1rem !important" }} />}
            onClick={addSchedule}
            sx={{
              color: "rgba(255,255,255,0.75)",
              borderColor: "rgba(255,255,255,0.2)",
              fontSize: "0.82rem",
              fontWeight: 500,
              textTransform: "none",
              borderRadius: "8px",
              "&:hover": { borderColor: "rgba(255,255,255,0.45)", bgcolor: "rgba(255,255,255,0.04)" },
            }}
          >
            Add New
          </Button>
          <Button
            variant="outlined"
            startIcon={<UploadFileIcon sx={{ fontSize: "1rem !important" }} />}
            onClick={() => setCsvModalOpen(true)}
            sx={{
              color: "rgba(255,255,255,0.75)",
              borderColor: "rgba(255,255,255,0.2)",
              fontSize: "0.82rem",
              fontWeight: 500,
              textTransform: "none",
              borderRadius: "8px",
              "&:hover": { borderColor: "rgba(255,255,255,0.45)", bgcolor: "rgba(255,255,255,0.04)" },
            }}
          >
            Import CSV
          </Button>
          <Button
            variant="contained"
            disabled={saved !== "Save"}
            onClick={() => updateBulkSchedulePS({ body: bulkArray })}
            sx={{
              bgcolor: "#FDB940",
              color: "#0d1117",
              fontWeight: 600,
              fontSize: "0.82rem",
              textTransform: "none",
              borderRadius: "8px",
              boxShadow: "none",
              px: "18px",
              "&:hover": { bgcolor: "#e8a830", boxShadow: "none" },
              "&.Mui-disabled": { bgcolor: "rgba(253,185,64,0.25)", color: "rgba(0,0,0,0.4)" },
            }}
          >
            {saved}
          </Button>
        </Box>
      </Box>

      {/* Rows */}
      {schedule_data?.map((schedule) => (
        <PeakShaveRow
          key={schedule.id}
          schedule={schedule}
          setSaved={setSaved}
          setBulkArray={setBulkArray}
          feederGroups={feederGroups}
          configurations={configurations}
          deletingId={deletingId}
          setDeletingId={setDeletingId}
        />
      ))}

      {/* Info bar */}
      <Box
        sx={{
          mt: "12px",
          px: "16px",
          py: "11px",
          backgroundColor: "#161B22",
          border: "1px solid rgba(255,255,255,0.06)",
          borderRadius: "10px",
          display: "flex",
          alignItems: "center",
          gap: "10px",
        }}
      >
        <InfoOutlinedIcon sx={{ fontSize: "0.95rem", color: "rgba(255,255,255,0.35)", flexShrink: 0 }} />
        <Typography sx={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.4)" }}>
          Times are operator local (SAST, UTC+02). Schedule dates are interpreted as{" "}
          <Box component="span" sx={{ color: "#e0e6f0", fontWeight: 600 }}>start 00:00:00</Box>
          {" → "}
          <Box component="span" sx={{ color: "#e0e6f0", fontWeight: 600 }}>end 21:59:59</Box>
          {" "}for the day range.
        </Typography>
      </Box>

      <CSVBulkUpdate
        open={csvModalOpen}
        onClose={() => setCsvModalOpen(false)}
        csvType="peak-shaving"
        onSuccess={() => { refetch(); setSaved("Save"); }}
      />
    </Box>
  );
}

function PeakShaveRow({ schedule, setSaved, setBulkArray, feederGroups, configurations, deletingId, setDeletingId }) {
  const [deleteSchedulePS] = useDeleteSchedulePSMutation();
  const saTimeOffset = 2 * 60 * 60 * 1000;

  const [startTime, setStartTime] = useState(dayjs("2023-01-01T" + schedule.start_time));
  const [endTime, setEndTime] = useState(dayjs("2023-01-01T" + schedule.end_time));
  const [day_type, setDayType] = useState(schedule.day);
  const [priorityVal, setPriorityVal] = useState(schedule.priority);
  const [targetVal, setTargetVal] = useState(schedule.target);
  const [startDate, setStartDate] = useState(dayjs(schedule.start_date));
  const [endDate, setEndDate] = useState(dayjs(schedule.end_date));
  const [selectedFeederGroups, setSelectedFeederGroups] = useState(schedule.feeder_group || []);
  const [selectedConfig, setSelectedConfig] = useState(schedule.configuration || "");

  const days = ["Weekdays", "Saturday", "Sunday", "All"];

  const updateBulk = (field, value) => {
    setBulkArray((prev) => prev.map((item) => item.id === schedule.id ? { ...item, [field]: value } : item));
    setSaved("Save");
  };

  const onChangeStart = (time) => {
    const utcTime = time.subtract(2, "hours");
    setStartTime(utcTime);
    updateBulk("start_time", utcTime.$H + ":" + utcTime.$m + ":" + utcTime.$s);
  };

  const onChangeEnd = (time) => {
    const utcTime = time.subtract(2, "hours");
    setEndTime(utcTime);
    updateBulk("end_time", utcTime.$H + ":" + utcTime.$m + ":" + utcTime.$s);
  };

  const onChangeStartDate = (date) => {
    const d = date.set("hour", 0).set("minute", 0).set("second", 0);
    setStartDate(d);
    updateBulk("start_date", d.format("YYYY-MM-DD HH:mm:ss"));
  };

  const onChangeEndDate = (date) => {
    const d = date.set("hour", 21).set("minute", 59).set("second", 59);
    setEndDate(d);
    updateBulk("end_date", d.format("YYYY-MM-DD HH:mm:ss"));
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ mb: "8px" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-end",
            gap: "8px",
            backgroundColor: "#161B22",
            border: "1px solid rgba(255,255,255,0.07)",
            borderRadius: "10px",
            px: "16px",
            py: "14px",
          }}
        >
          {/* Schedule ID */}
          <Col label="Schedule" width={52}>
            <Typography sx={{ fontSize: "0.88rem", fontWeight: 700, color: "#e0e6f0", lineHeight: "34px" }}>
              PS {schedule.id}
            </Typography>
          </Col>

          {/* Priority */}
          <Col label="Priority" width={56}>
            <TextField
              value={priorityVal}
              size="small"
              onChange={(e) => { setPriorityVal(e.target.value); updateBulk("priority", e.target.value); }}
              sx={INPUT_SX}
            />
          </Col>

          {/* Day */}
          <Col label="Day" width={100}>
            <FormControl size="small" fullWidth>
              <Select
                value={day_type}
                onChange={(e) => { setDayType(e.target.value); updateBulk("day", e.target.value); }}
                sx={SELECT_SX}
                MenuProps={MENU_PROPS}
              >
                {days.map((d) => <MenuItem key={d} value={d}>{d}</MenuItem>)}
              </Select>
            </FormControl>
          </Col>

          {/* Start Time */}
          <Col label="Start Time" width={108}>
            <TimePicker
              value={startTime.add(saTimeOffset, "ms")}
              onChange={onChangeStart}
              ampm={false}
              slotProps={{ textField: { size: "small", sx: INPUT_SX } }}
            />
          </Col>

          {/* End Time */}
          <Col label="End Time" width={108}>
            <TimePicker
              value={endTime.add(saTimeOffset, "ms")}
              onChange={onChangeEnd}
              ampm={false}
              slotProps={{ textField: { size: "small", sx: INPUT_SX } }}
            />
          </Col>

          {/* Target */}
          <Col label="Target (VA)" width={88}>
            <TextField
              value={targetVal}
              size="small"
              onChange={(e) => { setTargetVal(e.target.value); updateBulk("target", e.target.value); }}
              sx={INPUT_SX}
            />
          </Col>

          {/* Start Date */}
          <Col label="Start Date" width={120}>
            <MobileDatePicker
              format="YYYY/MM/DD"
              value={startDate}
              onChange={onChangeStartDate}
              slotProps={{ textField: { size: "small", fullWidth: true, sx: INPUT_SX } }}
            />
          </Col>

          {/* End Date */}
          <Col label="End Date" width={120}>
            <MobileDatePicker
              format="YYYY/MM/DD"
              value={endDate}
              onChange={onChangeEndDate}
              slotProps={{ textField: { size: "small", fullWidth: true, sx: INPUT_SX } }}
            />
          </Col>

          {/* Feeder Groups */}
          <Col label="Feeder Groups" flex={1.5} minWidth={150}>
            <FormControl size="small" fullWidth>
              <Select
                multiple
                value={selectedFeederGroups}
                onChange={(e) => { setSelectedFeederGroups(e.target.value); updateBulk("feeder_group", e.target.value); }}
                input={<OutlinedInput />}
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                    {selected.map((id) => {
                      const fg = feederGroups.find((g) => g.id === id);
                      return (
                        <Chip
                          key={id}
                          label={fg?.name || id}
                          size="small"
                          sx={{
                            bgcolor: "rgba(253,185,64,0.12)",
                            color: "#FDB940",
                            border: "1px solid rgba(253,185,64,0.25)",
                            borderRadius: "6px",
                            fontSize: "0.76rem",
                            height: "22px",
                          }}
                        />
                      );
                    })}
                  </Box>
                )}
                sx={{ ...SELECT_SX, "& .MuiSelect-select": { padding: "6px 12px", minHeight: "34px !important" } }}
                MenuProps={MENU_PROPS}
              >
                {feederGroups.map((fg) => (
                  <MenuItem key={fg.id} value={fg.id}>
                    <Checkbox
                      checked={selectedFeederGroups.indexOf(fg.id) > -1}
                      size="small"
                      sx={{ color: "rgba(255,255,255,0.4)", "&.Mui-checked": { color: "#FDB940" }, p: "4px", mr: "4px" }}
                    />
                    <ListItemText primary={fg.name} primaryTypographyProps={{ fontSize: "0.88rem", color: "#e0e6f0" }} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Col>

          {/* Configuration */}
          <Col label="Configuration" flex={1} minWidth={190}>
            <FormControl size="small" fullWidth>
              <Select
                value={selectedConfig}
                onChange={(e) => { setSelectedConfig(e.target.value); updateBulk("configuration", e.target.value || null); }}
                sx={SELECT_SX}
                MenuProps={MENU_PROPS}
              >
                <MenuItem value=""><em style={{ color: "rgba(255,255,255,0.4)", fontStyle: "normal" }}>None</em></MenuItem>
                {configurations.map((c) => (
                  <MenuItem key={c.id} value={c.id}>{c.description || `Config #${c.id}`}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Col>

          {/* Delete */}
          <Box sx={{ flexShrink: 0, alignSelf: "flex-end" }}>
            <IconButton
              size="small"
              onClick={() => setDeletingId(schedule.id)}
              sx={{
                color: "rgba(255,255,255,0.3)",
                "&:hover": { color: "#f87171", bgcolor: "rgba(248,113,113,0.08)" },
              }}
            >
              <DeleteIcon sx={{ fontSize: "1.1rem" }} />
            </IconButton>
          </Box>
        </Box>

        {/* Inline delete confirmation */}
        <Collapse in={deletingId === schedule.id}>
          <Box
            sx={{
              mt: "4px",
              px: "20px", py: "12px",
              backgroundColor: "rgba(248,113,113,0.06)",
              border: "1px solid rgba(248,113,113,0.2)",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography sx={{ fontSize: "0.84rem", color: "rgba(255,255,255,0.65)" }}>
              Delete schedule <strong style={{ color: "#e0e6f0" }}>PS {schedule.id}</strong>? This cannot be undone.
            </Typography>
            <Box sx={{ display: "flex", gap: "8px" }}>
              <Button
                size="small"
                onClick={() => setDeletingId(null)}
                sx={{ fontSize: "0.8rem", textTransform: "none", color: "rgba(255,255,255,0.5)", "&:hover": { bgcolor: "rgba(255,255,255,0.06)" } }}
              >
                Cancel
              </Button>
              <Button
                size="small"
                onClick={() => { deleteSchedulePS({ schedule_id: schedule.id }); setDeletingId(null); }}
                sx={{
                  fontSize: "0.8rem", textTransform: "none", color: "#f87171",
                  border: "1px solid rgba(248,113,113,0.35)", borderRadius: "6px", px: "10px",
                  "&:hover": { bgcolor: "rgba(248,113,113,0.1)", borderColor: "#f87171" },
                }}
              >
                Delete
              </Button>
            </Box>
          </Box>
        </Collapse>
      </Box>
    </LocalizationProvider>
  );
}
